/* istanbul ignore file */

import React from "react";
import {
  capitalize,
  cloneDeep,
  each,
  has,
  identity,
  includes,
  isEmpty,
  isNull,
  mapValues,
  merge,
  parseInt,
  pickBy,
  omit,
  get,
  forEach,
  intersection,
  keys,
  difference,
  isArray,
} from "lodash";
import moment from "moment";
import { datadogRum } from "@datadog/browser-rum";
import { toast } from "react-toastify";
import { Icon } from "@px/px_design_system";

import {
  commaSeparatedStringToArray,
  splitCommaSeparatedStringToArray,
} from "../../../../../utils";
import { dayPartWeekDay } from "../../OrderManagement/OrderDetails/PlacementMetaData/placementMapping";
import {
  ListFieldValidation,
  crossFieldValidation,
  int32MaxDateValidation,
  missingColValidation,
  valueRangeValidation,
  uuidValidation,
  validateNetwork,
  countLengthValidation,
  textRangeValidation,
  isValidAdFormatType,
  isValidAspectRatioType,
  isValidateDate,
  isValidEids,
  errorData,
  getAvailableResources,
  getLocationData,
  validateMinMaxDuration,
  isValidTime,
  isValidWeekdays,
  validateDaypartFields,
  validateMinMaxTime,
  validateDateTimeFields,
  validateNetworkName,
  validateSetcapFields,
  validateVenueOpenOOHCategory,
  venueList,
  validateGeoData,
  getGeoTargetingData,
  warningData,
  validateEntity,
  validateListFields,
} from "./fieldValidation";
import ErrorMessages from "../../../DisplayError/ErrorMessages";

import "./style.css";

const flatten = require("flat");
const { unflatten } = require("flat");

let geoTargetingApiData;
let geoTargetingApiDataAllRow = [];

export const getGeoCoutryData = async (targeting) => {
  geoTargetingApiData = await getGeoTargetingData(pickBy(targeting, identity));
  geoTargetingApiData?.forEach((data) => {
    let newdata = geoTargetingApiDataAllRow.find(
      (element) => element.country === data.country
    );
    if (newdata === undefined) geoTargetingApiDataAllRow.push(data);
  });

  return geoTargetingApiData;
};

const isDataInfoUndefined = (data, path) => {
  let isUndefined = false;
  if (get(data, path, {}).info === undefined) {
    isUndefined = true;
  }
  return isUndefined;
};

const ToastMsg = (count) => (
  <div className="toast--body">
    <Icon icon={["fas", "exclamation-triangle"]} size="2x" />
    <div>
      <strong>{count} Locations Updated!</strong>
      <div>
        Information for {count} adunits is normalized across DMA, State, Country
        - please see below for more details
      </div>
    </div>
  </div>
);

const prepareOptions = (field, mappingModel, humanReadableOptions = false) => {
  const mappingObj = mappingModel[field];
  const optionsArray = [];
  for (const key in mappingObj) {
    if (Object.prototype.hasOwnProperty.call(mappingObj, key)) {
      optionsArray.push({
        value: key,
        label: humanReadableOptions ? mappingObj[key] : key,
      });
    }
  }
  return optionsArray;
};

const regexMatchesValidator = (regex, error) => ({
  validate: "regex_matches",
  regex,
  regexFlags: { ignoreCase: true },
  error,
});

const beforeAfterSpaceValidation = () => {
  return regexMatchesValidator(
    "^((?!^\\s+|\\s+$).)*$",
    "Space is not allowed before OR after the name"
  );
};

// Trim leading zeros
const trimZeros = (ratio) => {
  let aspectRatio = ratio;
  if (!isEmpty(ratio) && ratio.includes(":")) {
    const splittedRatio = ratio.split(":");
    aspectRatio = `${splittedRatio[0].replace(
      /^0+/,
      ""
    )}:${splittedRatio[1].replace(/^0+/, "")}`;
  }
  return aspectRatio;
};

// Method to get formatted date
// User can enter any format so this will convert that date to standard ISO
const getFormattedTime = (inputTime) => {
  let setTime = "";
  if (inputTime) {
    setTime = moment(
      inputTime.split(" ")[0],
      ["h", "h:m", "hA", "h:ma", "h:mA"],
      true
    ).isValid()
      ? moment(inputTime, ["h:m A"]).format("h:mm A")
      : inputTime;
  }
  return setTime;
};

const getFormattedTimeForList = (inputTimeList) => {
  let formattedList = "";
  if (inputTimeList) {
    let listWithArray = inputTimeList.split(",");
    formattedList = listWithArray
      .map((value) => {
        return getFormattedTime(value.trim());
      })
      .join(",");
  }
  return formattedList;
};

const getFormattedAspectRatios = (aspectRatioList) => {
  let formattedRatios = "";
  if (aspectRatioList) {
    const ratios = aspectRatioList.split(",");
    each(ratios, (ratio, index) => {
      formattedRatios =
        index === ratios.length - 1
          ? `${formattedRatios}${trimZeros(ratio.trim())}`
          : `${formattedRatios}${trimZeros(ratio.trim())},`;
    });
  }
  return formattedRatios;
};

let totalRecords;
export const getTotalcount = (totalCount) => {
  totalRecords = totalCount;
};

let getFormattedCount = 0;
let numOfRecords = 0;
const recordFormat = (recordRef, locationdata) => {
  numOfRecords++;
  let isAutoFormatted = false;
  const record = {
    ...recordRef,
    "location.dma_code": parseInt(recordRef["location.dma_code"]) || null,
  };
  const out = [];
  out["asset.aspect_ratio"] = {
    value: record["asset.aspect_ratio"]
      ? trimZeros(record["asset.aspect_ratio"])
      : "",
  };

  out.start_time = { value: getFormattedTime(record.start_time) };

  out.end_time = { value: getFormattedTime(record.end_time) };

  out["dayparts.start_time"] = {
    value: getFormattedTimeForList(record["dayparts.start_time"]),
  };

  out["dayparts.end_time"] = {
    value: getFormattedTimeForList(record["dayparts.end_time"]),
  };

  out["aspect_ratios"] = {
    value: getFormattedAspectRatios(record["aspect_ratios"]),
  };

  if (has(locationdata, "lat")) {
    each(locationdata, (val, key) => {
      out[`location.${key}`] = {
        value: val || "",
      };

      if (record[`location.${key}`] !== val) {
        isAutoFormatted = true;
        out[`location.${key}`] = {
          ...out[`location.${key}`],
          info: [
            {
              message: `Based on lat/lon, ${key} is updated from ${
                isEmpty(recordRef[`location.${key}`])
                  ? "'blank'"
                  : record[`location.${key}`]
              } to ${
                isNull(val) || val === "" ? "'blank'" : val
              }. If this is incorrect, check lat/lon submitted and try again"`,
              level: "warning",
            },
          ],
        };
      }
    });
  }

  if (isAutoFormatted) {
    getFormattedCount++;
  }

  if (totalRecords === numOfRecords && getFormattedCount > 0) {
    toast.warn(ToastMsg(getFormattedCount), {
      toastId: "location",
      position: "top-center",
    });
    numOfRecords = 0;
    getFormattedCount = 0;
  }

  return out;
};

const networkValidations = async (orgId, record, item, mode) => {
  const networkIdError = await validateNetwork(
    record[item.key],
    record["network_name"],
    orgId,
    mode
  );
  const networkNameError = record["network_name"]
    ? validateNetworkName(record[item.key], record["network_name"])
    : "";
  if (networkIdError) {
    return { key: item.key, value: networkIdError };
  } else if (networkNameError) {
    return { key: "network_name", value: networkNameError };
  } else {
    return "";
  }
};

const statusValidation = async (orgId, record, item) => {
  const payload = {
    model: "AdUnit",
    names: [record["name"]],
    show_fields: ["status", "name"],
  };
  const serverResults = await getAvailableResources(orgId, payload);

  const inReviewRecords = serverResults.filter((item) => item.status === 6);

  if (serverResults.includes("API Failed")) {
    return { key: "status", value: ErrorMessages.API_FAILED_MESSAGE };
  } else {
    if (
      (inReviewRecords.length &&
        record[item.key] !== "6") /* Unset from review */ ||
      ((!inReviewRecords.length || !serverResults.length) &&
        record[item.key] === "6") /* Set review for new records */
    ) {
      return {
        key: "status",
        value:
          'You are not permitted to set/reset "In-Review" status. Refer help article or please contact PX Support for more.',
      };
    }
    return "";
  }
};

const checkIfDealExist = async (orgId, record, item, mode) => {
  const payload = {
    model: "Deal",
    names: [record[item.key]],
    show_names: "true",
  };
  const getExistingDeals = await getAvailableResources(orgId, payload);
  const availableNamesList = getExistingDeals.resources_available_names;
  const exactDealExist = includes(availableNamesList, record[item.key]);

  if (
    record[item.key] &&
    Array.isArray(getExistingDeals) &&
    includes(getExistingDeals, "API Failed")
  ) {
    return { key: "name", value: ErrorMessages.API_FAILED_MESSAGE };
  } else if (!exactDealExist) {
    return {
      key: "name",
      value:
        "Deal does not exist in the organization. Please enter a valid deal name.",
    };
  }
  return "";
};

const externalApiValidation = async (orgId, record, item, mode) => {
  if (
    item.key === "status" &&
    item.model === "AdUnit" &&
    mode === "change" &&
    record[item.key]
  ) {
    return await statusValidation(orgId, record, item);
  }

  if (item.key === "name" && item.model === "ArchiveDeal") {
    return await checkIfDealExist(orgId, record, item, mode);
  }

  /* Validates if network_id exist in organization or not */
  if (item.key === "network_id") {
    return await networkValidations(orgId, record, item, mode);
  }

  const keyListWithoutQuotes = ["targeting.keywords", "keywords"];
  const inputList = includes(keyListWithoutQuotes, item.key)
    ? commaSeparatedStringToArray(record[item.key])
    : splitCommaSeparatedStringToArray(record[item.key]);
  const payload = {
    model: item.model,
    names: inputList,
    show_names: "true",
  };
  let changeValues = [];
  let validChoices = await getAvailableResources(orgId, payload);
  let invalidList = difference(
    inputList,
    validChoices.resources_available_names
  );
  if (!isEmpty(invalidList)) {
    changeValues.push({
      key: item.key,
      value: `${invalidList.join(", ")} ${
        item.label
      } does not exists in this organization.`,
    });
  }
  return changeValues;
};

// Prime method which trigger validation
// (It includes validation of all those fields which are tricky to
// validate through normal regex validation)

const validateData = async (
  record,
  mode,
  orgId,
  choicesArray,
  validationFields,
  uploadType,
  isSuperUser
) => {
  const out = [];
  Object.keys(record).forEach((key) => {
    if (
      typeof record[key] === "string" &&
      record[key] !== "" &&
      record[key].trim() === ""
    ) {
      out[key] = {
        value: record[key].trim(),
      };
      record[key] = record[key].trim();
    } else {
      out[key] = {
        value: record[key],
      };
    }
  });
  // formattedRecord should be passed for fields which got formatted
  const latlong = {};
  latlong["location.lat"] = record["location.lat"];
  latlong["location.lon"] = record["location.lon"];
  latlong["location.city"] = record["location.city"];
  latlong["location.country"] = record["location.country"];
  latlong["location.dma"] = record["location.dma"];
  latlong["location.dma_code"] = record["location.dma_code"];
  latlong["location.region"] = record["location.region"];
  latlong["location.zip"] = record["location.zip"];
  const targeting = {};
  targeting["targeting.geo.country"] = record["targeting.geo.country"];
  const locationdata = await getLocationData(
    pickBy(latlong, identity),
    record["name"] || ""
  );
  geoTargetingApiData = await getGeoTargetingData(pickBy(targeting, identity));
  geoTargetingApiData?.forEach((data) => {
    let newdata = geoTargetingApiDataAllRow.find(
      (element) => element.country === data.country
    );
    if (newdata === undefined) geoTargetingApiDataAllRow.push(data);
  });
  const formattedRecords = recordFormat(record, locationdata);
  validationFields.forEach(async (data) => {
    // Trim whitespaces and update boolean fields
    if (record[data.field] === undefined && data.type === "boolean") {
      out[data.field] = {
        value: "",
      };
    }
    const currencyFields = [
      "auction.bidfloorcur",
      "planning.rate_cur",
      "bidfloorcur",
    ];
    const daypartsField = [
      "dayparts.weekday",
      "dayparts.start_time",
      "dayparts.end_time",
    ];
    const dateFields = ["end_date", "end_time", "start_date", "start_time"];
    const setCapFields = ["daily", "lifetime"];
    currencyFields.map((item) => {
      if (record[item] === null) {
        out[item] = {
          ...out[data.field],
          info: errorData(
            "The selected currency is not a valid choice. Please select a value from existing options."
          ),
        };
      }
    });
    if (
      !isEmpty(record[data.field]) ||
      dateFields.includes(data.field) ||
      daypartsField.includes(data.field) ||
      setCapFields.includes(data.field)
    ) {
      if (data.type === "list") {
        const availableChoices = choicesArray(data.field);
        out[data.field] = {
          ...out[data.field],
          info: ListFieldValidation(record, data.field, availableChoices),
        };
      } else if (
        data.type === "crossMinMax" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: validateMinMaxDuration(record),
        };
      } else if (data.type === "required_column") {
        out[data.field] = {
          ...out[data.field],
          info: missingColValidation(
            data.field,
            record[data.field],
            record,
            data.params
          ),
        };
      } else if (
        data.type === "cross" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: crossFieldValidation(
            record,
            data.field,
            !isEmpty(get(formattedRecords, `${data.field}`))
              ? formattedRecords[data.field]?.value
              : record[data.field],
            uploadType
          ),
        };
      } else if (data.type === "range") {
        out[data.field] = {
          ...out[data.field],
          info: valueRangeValidation(data.min, data.max, record[data.field]),
        };

        if (
          (data.field === "location.lat" || data.field === "location.lon") &&
          !has(locationdata, "lat")
        ) {
          out[data.field] = {
            ...out[data.field],
            info: locationdata,
          };
        }
      } else if (data.type === "uuid") {
        out[data.field] = {
          ...out[data.field],
          info: uuidValidation(record[data.field]),
        };
      } else if (
        (data.type === "countLength" ||
          data.type === "textLength" ||
          data.type === "digitsBeforeDecimal" ||
          data.type === "isGreaterThan") &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: countLengthValidation(
            data.type,
            record[data.field],
            data.limit
          ),
        };
      } else if (data.type === "textRange") {
        out[data.field] = {
          ...out[data.field],
          info: textRangeValidation(
            record[data.field],
            data.minLength,
            data.maxLength
          ),
        };
      } else if (data.type === "date" && isDataInfoUndefined(out, data.field)) {
        out[data.field] = {
          ...out[data.field],
          info: isValidateDate(record[data.field]),
        };
      } else if (data.type === "time") {
        out[data.field] = {
          ...out[data.field],
          info: isValidTime(formattedRecords[data.field].value),
        };
      } else if (data.type === "adformats") {
        out[data.field] = {
          ...out[data.field],
          info: isValidAdFormatType(record[data.field]),
        };
      } else if (data.type === "aspectRatios") {
        out[data.field] = {
          ...out[data.field],
          info: isValidAspectRatioType(formattedRecords[data.field]),
        };
      } else if (data.type === "eids") {
        out[data.field] = {
          ...out[data.field],
          info: isValidEids(record[data.field]),
        };
      } else if (data.type === "daypart_weekdays") {
        out[data.field] = {
          ...out[data.field],
          info: isValidWeekdays(record[data.field]),
        };
      } else if (
        data.type === "daypart_field" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: validateDaypartFields(record, data.field),
        };
      } else if (
        data.type === "time_min_max" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: validateMinMaxTime(formattedRecords),
        };
      } else if (
        data.type === "date-time-compare" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: validateDateTimeFields(record, formattedRecords),
        };
      } else if (
        data.type === "int32-max-date-compare" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: int32MaxDateValidation(record[data.field]),
        };
      } else if (
        data.type === "set-cap" &&
        isDataInfoUndefined(out, data.field)
      ) {
        out[data.field] = {
          ...out[data.field],
          info: validateSetcapFields(record),
        };
      } else if (data.type === "targeting.venue_openooh_category") {
        out[data.field] = {
          ...out[data.field],
          info: validateVenueOpenOOHCategory(record[data.field]),
        };
      } else if (data.type === "targeting.geo_targeting") {
        out[data.field] = {
          ...out[data.field],
          info: validateGeoData(
            record[data.field],
            data.field,
            geoTargetingApiData
          ),
        };
      } else if (data.type === "entityName") {
        out[data.field] = {
          ...out[data.field],
          info: validateEntity(
            record[data.field],
            data.field,
            data?.entityLengthCount
          ),
        };
      } else if (data.type === "listField") {
        out[data.field] = {
          ...out[data.field],
          info: validateListFields(record[data.field]),
        };
      }
    }
  });
  let externalValidationFields = [];
  if (uploadType === "Ad Units") {
    externalValidationFields = [
      { key: "keywords", label: "keywords", model: "Keyword" },
    ];
    /* Validate for Pub users */
    if (!isSuperUser) {
      externalValidationFields = [
        ...externalValidationFields,
        {
          key: "status",
          label: "AdUnit",
          model: "AdUnit",
          validateOnInit: "false",
        },
      ];
    }
    if (!get(out, "network_id", {}).info) {
      externalValidationFields = [
        ...externalValidationFields,
        { key: "network_id", label: "Network Id" },
      ];
    }
  } else if (uploadType === "Archive Deal") {
    externalValidationFields = [
      {
        key: "name",
        label: "deal",
        model: "ArchiveDeal",
        validateOnInit: "false",
      },
    ];
  }

  const apiValidation = () => {
    const arr = [];
    for (const item of externalValidationFields) {
      if (record[item.key]) {
        if (mode === "init" && item.validateOnInit === "false") {
          /* Do not perform validations on initHook as its trigged from fieldHook */
        } else if (
          item.key === "keywords" ||
          item.key === "targeting.keywords"
        ) {
          if (isDataInfoUndefined(out, item.key)) {
            arr.push(externalApiValidation(orgId, record, item));
          }
        } else arr.push(externalApiValidation(orgId, record, item, mode));
      }
    }
    return arr;
  };
  const validateFields = await Promise.all(apiValidation());
  for (const item of validateFields) {
    if (item[0]?.key === "keywords" || item[0]?.key === "targeting.keywords") {
      const warningMsg = item[0]?.value;
      out[item[0]?.key] = {
        ...out[item[0]?.key],
        info: warningData(
          warningMsg.concat(
            " ",
            "Once the records successfully submitted then these new keywords gets created in the organization"
          )
        ),
      };
    } else if (isArray(item) && item.length) {
      out[item[0].key] = {
        ...out[item[0].key],
        info: item.map((element) => ({
          message: element.value,
          level: "error",
        })),
      };
    } else {
      out[item.key] = {
        ...out[item.key],
        info: errorData(item.value),
      };
    }
  }
  return merge(out, formattedRecords);
};

// Date Formatter to ISO-8061
const formatDate = (uploaderObject) => {
  const objectToUpload = cloneDeep(uploaderObject);
  objectToUpload["asset.aspect_ratio"] = trimZeros(
    uploaderObject["asset.aspect_ratio"]
  );
  if (
    !isEmpty(uploaderObject.start_date) &&
    !moment(uploaderObject.start_date, "YYYY-MM-DD", true).isValid()
  ) {
    const isoDate = moment(objectToUpload.start_date).format("YYYY-MM-DD");
    objectToUpload.start_date = isoDate === "Invalid date" ? null : isoDate;
  }
  return objectToUpload;
};

const getFormattedDateandTime = (inputDate) => {
  let settDateTime = "";
  if (inputDate) {
    settDateTime = moment(inputDate).isValid()
      ? moment(inputDate).format("YYYY-MM-DD HH:mm:ss")
      : inputDate;
  }
  return settDateTime;
};

const getUnixTimeStamp = (datAndTime) => {
  let unixFormat = !isEmpty(datAndTime)
    ? (
        moment.utc(datAndTime, "YYYY-MM-DD HH:mm:ss").valueOf() / 1000
      ).toString()
    : null;
  return unixFormat;
};

const dateAndTimeConversion = (uploaderObject, objectToUpload, date, time) => {
  let dateAndTime = `${get(uploaderObject, date)} ${get(uploaderObject, time)}`;
  objectToUpload[date] = getUnixTimeStamp(getFormattedDateandTime(dateAndTime));
};

// Combine date and time together and convert it to UnixTimeStamp
const formatTimeWithDate = (uploaderObject) => {
  let objectToUpload = cloneDeep(uploaderObject);
  const startDateAndTimeNotEmpty =
    !isEmpty(uploaderObject.start_date) && !isEmpty(uploaderObject.start_time);
  const endDateAndTimeNotEmpty =
    !isEmpty(uploaderObject.end_date) && !isEmpty(uploaderObject.end_time);

  if (startDateAndTimeNotEmpty) {
    dateAndTimeConversion(
      uploaderObject,
      objectToUpload,
      "start_date",
      "start_time"
    );
  }

  if (endDateAndTimeNotEmpty) {
    dateAndTimeConversion(
      uploaderObject,
      objectToUpload,
      "end_date",
      "end_time"
    );
  }

  objectToUpload = omit(objectToUpload, ["start_time", "end_time"]);
  return objectToUpload;
};

const isDaypartFieldExists = (uploaderObject) => {
  const isExist =
    uploaderObject.hasOwnProperty("dayparts.weekday") &&
    uploaderObject.hasOwnProperty("dayparts.start_time") &&
    uploaderObject.hasOwnProperty("dayparts.end_time");
  return isExist;
};

const formatTimeTwentyFourHourClock = (inputTime) => {
  if (moment(inputTime, ["h:mmA"], true).isValid()) {
    return moment(inputTime, ["h:mmA"]).format("HH:mm:ss");
  } else {
    return inputTime;
  }
};

const formatDaypartFields = (uploaderObject) => {
  let objectToUpload = cloneDeep(uploaderObject);
  if (isDaypartFieldExists(uploaderObject)) {
    const dypartFields = [
      "dayparts.weekday",
      "dayparts.start_time",
      "dayparts.end_time",
    ];
    let daypartFieldsValue = [];
    let weekDayValue = commaSeparatedStringToArray(
      get(objectToUpload, "dayparts.weekday", "")
    ).map((value) => dayPartWeekDay[value.toLowerCase()]);
    const startTimeValue = commaSeparatedStringToArray(
      get(objectToUpload, "dayparts.start_time", "")
    );
    const endTimeValue = commaSeparatedStringToArray(
      get(objectToUpload, "dayparts.end_time", "")
    );
    const isDaypartFieldNotEmpty =
      !isEmpty(weekDayValue) &&
      !isEmpty(startTimeValue) &&
      !isEmpty(endTimeValue);
    const isDaypartFieldsEqualLength =
      weekDayValue.length === startTimeValue.length &&
      weekDayValue.length === endTimeValue.length;

    if (isDaypartFieldNotEmpty && isDaypartFieldsEqualLength) {
      for (let i = 0; i < weekDayValue.length; i++) {
        daypartFieldsValue.push({
          weekday: weekDayValue[i],
          start_time: formatTimeTwentyFourHourClock(startTimeValue[i]),
          end_time: formatTimeTwentyFourHourClock(endTimeValue[i]),
        });
      }
    }
    Object.keys(objectToUpload).forEach((key) => {
      if (dypartFields.includes(key)) delete objectToUpload[key];
    });
    objectToUpload["dayparts"] = daypartFieldsValue;
  }
  return objectToUpload;
};

const formatDealPriorityFieldPayload = (
  uploaderObject,
  dealPriorityOptions
) => {
  let objectToUpload = cloneDeep(uploaderObject);
  if (has(objectToUpload, "priority")) {
    const selectedPriority = get(objectToUpload, "priority");
    objectToUpload["priority"] = Number(selectedPriority);
  }
  return objectToUpload;
};

const fetchSelectedCountrydata = (type, countryName) => {
  if (geoTargetingApiDataAllRow && geoTargetingApiDataAllRow.length >= 1) {
    return geoTargetingApiDataAllRow.find(
      (data) => data.country === countryName
    )[`${type}`];
  }
};

const getIntersectionData = (inputData, type, countryName) => {
  let apiData = fetchSelectedCountrydata(type, countryName);
  if (type === "dma") {
    let apiData_dma = keys(apiData);
    let interSecDemaName = intersection(inputData, apiData_dma);
    //code to convert dma value to key.
    let interSecDemaCode = interSecDemaName.map(
      (inpDmaName) => apiData[inpDmaName]
    );
    return interSecDemaCode;
  }
  return intersection(inputData, apiData);
};

const createGeoStructure = (countries) =>
  commaSeparatedStringToArray(countries).map((country) => {
    return { country: country };
  });

const formatGeoTargetingPayload = (objectToUpload) => {
  let formatedGeoTargetingList = createGeoStructure(
    objectToUpload["targeting.geo.country"]
  );
  formatedGeoTargetingList = formatedGeoTargetingList.map((geo) => {
    const regionValues = get(objectToUpload, "targeting.geo.region", "");
    const zipValues = get(objectToUpload, "targeting.geo.zip", "");
    const dmaValues = get(objectToUpload, "targeting.geo.dma", "");
    let intersectionRegion = getIntersectionData(
      commaSeparatedStringToArray(regionValues),
      "region",
      geo.country
    );
    let intersectionZip = getIntersectionData(
      commaSeparatedStringToArray(zipValues),
      "zip",
      geo.country
    );
    let intersectionDma_code = getIntersectionData(
      splitCommaSeparatedStringToArray(dmaValues),
      "dma",
      geo.country
    );
    return {
      ...geo,
      region: intersectionRegion,
      zip: intersectionZip,
      dma_code: intersectionDma_code,
    };
  });
  return formatedGeoTargetingList;
};

const formatvenueTargetingPayload = (objectToUpload) => {
  let venueListIds = [];
  const venueOpenOOHValue = get(
    objectToUpload,
    "targeting.venue_openooh_category",
    ""
  );
  const getVenueList = venueList;
  forEach(commaSeparatedStringToArray(venueOpenOOHValue), (list) => {
    if (getVenueList.find((i) => i.label === list))
      venueListIds.push(getVenueList.find((i) => i.label === list).value);
  });
  return venueListIds;
};

const deleteDuplicateKeysForTargeting = (objectToUpload) => {
  delete objectToUpload["targeting.geo.country"];
  delete objectToUpload["targeting.geo.region"];
  delete objectToUpload["targeting.geo.zip"];
  delete objectToUpload["targeting.geo.dma"];
  delete objectToUpload["targeting.venue_openooh_category"];
  delete objectToUpload["targeting.keywords"];
};

const isInputDataPresent = (inputData, fieldName) => {
  return (
    inputData &&
    has(inputData, `${fieldName}`) &&
    get(inputData, `${fieldName}`) !== null &&
    get(inputData, `${fieldName}`) !== ""
  );
};

const formatPlacementTargetingPayload = (uploaderObject) => {
  let objectToUpload = cloneDeep(uploaderObject);
  //formatting for targeting.venue_openooh
  if (has(objectToUpload, "targeting.venue_openooh_category")) {
    objectToUpload["targeting"] = {
      venue_openooh_category: formatvenueTargetingPayload(objectToUpload),
    };
  }
  //formatting geo targeting
  if (has(objectToUpload, "targeting.geo.country")) {
    if (isInputDataPresent(objectToUpload, "targeting.geo.country")) {
      objectToUpload["targeting"] = {
        ...objectToUpload["targeting"],
        geo: formatGeoTargetingPayload(objectToUpload),
      };
    } else {
      objectToUpload["targeting"] = { ...objectToUpload["targeting"], geo: [] };
    }
  }
  //formatting for targeting.keywords
  if (has(objectToUpload, "targeting.keywords")) {
    objectToUpload["targeting"] = {
      ...objectToUpload["targeting"],
      keywords: commaSeparatedStringToArray(
        get(objectToUpload, "targeting.keywords")
      ),
    };
  }
  deleteDuplicateKeysForTargeting(objectToUpload);
  return objectToUpload;
};

const prepareObjectToUpload = (uploaderObject, model, dealPriorityOptions) => {
  if (uploaderObject) {
    let uploadObject = formatDate(uploaderObject);
    let updatedUploadObject = formatTimeWithDate(uploadObject);
    updatedUploadObject = formatDaypartFields(updatedUploadObject);
    updatedUploadObject = formatDealPriorityFieldPayload(
      updatedUploadObject,
      dealPriorityOptions
    );
    updatedUploadObject = formatPlacementTargetingPayload(updatedUploadObject);
    if (has(updatedUploadObject, "keywords")) {
      updatedUploadObject["keywords"] = commaSeparatedStringToArray(
        updatedUploadObject["keywords"]
      );
    }
    // The default value for the asset.screen_count field is 1, so in the adunit model file, it is also set to 1.
    // Since the Pub's can download an empty CSV, it should contain an empty value in adunit model file.
    // This approach is used to handle the default value of 1.
    if (
      has(updatedUploadObject, "asset.screen_count") &&
      isEmpty(updatedUploadObject["asset.screen_count"])
    ) {
      updatedUploadObject["asset.screen_count"] = "1";
    }
    const uploadObjectFlat = mapValues(
      flatten(updatedUploadObject, { safe: true }),
      (val) => (val === null || val === undefined ? "" : val)
    );
    const uploadObjectFlatWithEmpty = pickBy(
      uploadObjectFlat,
      (value) => value === ""
    );

    const flatModel = cloneDeep(model);
    const flatModelWithoutEmpty = pickBy(flatModel, (value) => value !== "");

    const mergedEmptyNullProperties = mapValues(
      uploadObjectFlatWithEmpty,
      (value, key) => {
        if (has(flatModelWithoutEmpty, key)) {
          return flatModelWithoutEmpty[key];
        }

        return "";
      }
    );
    updatedUploadObject = unflatten({
      ...uploadObjectFlat,
      ...mergedEmptyNullProperties,
    });
    return updatedUploadObject;
  }
};

const triggerDDActionEvent = ({
  endTime,
  startTime,
  uploadCount,
  updateCount,
  uploadType,
}) => {
  // time taken (in milliseconds) to upload
  const timeTaken = endTime - startTime;
  if (uploadCount !== 0 || updateCount !== 0) {
    datadogRum.addAction(`click on ${capitalize(uploadType)} upload`, {
      uploadCount: uploadCount,
      updateCount: updateCount,
      uploadTime: timeTaken,
    });
  }
};

/* Remove when flatfile v2 is deprecated */
const uploadProgress = (progressBarUpdate, data, index) => {
  if (data) {
    progressBarUpdate(
      `${Math.round((index / data.length) * 100)} % uploaded...`
    );
  }
};

const uploadProgressBar = (
  progressBarUpdate,
  data,
  index,
  isBatchProcessEnabled = false
) => {
  const progressValue = isBatchProcessEnabled
    ? Math.round((index / data) * 100)
    : Math.round((index / data.length) * 100);
  if (data) {
    progressBarUpdate(progressValue, "Uploading...");
  }
};

export {
  prepareOptions,
  prepareObjectToUpload,
  regexMatchesValidator,
  triggerDDActionEvent,
  uploadProgress,
  uploadProgressBar,
  validateData,
  beforeAfterSpaceValidation,
};
